import { useQuery } from '@tanstack/react-query';

export interface PortalPagina {
  id: string;
  slug: string;
  titulo: string;
  hero_titulo: string | null;
  hero_descricao: string | null;
  hero_imagem_url: string | null;
  meta_title: string | null;
  meta_description: string | null;
  meta_keywords: string | null;
  og_image_url: string | null;
  canonical_url: string | null;
  noindex: boolean;
  jsonld_data: Record<string, unknown>[] | null;
  ordem: number;
  ativo: boolean;
  updated_at: string;
}

export interface PortalSecao {
  id: string;
  pagina_id: string;
  slug_secao: string;
  titulo: string | null;
  subtitulo: string | null;
  tipo: string;
  conteudo: Record<string, unknown>;
  ordem: number;
  ativo: boolean;
}

/**
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend. Não existe rota de
 * conteúdo do portal público na API (`apps/api/src/modulos/` só tem `eu`, `govbr` e
 * `senha`), e a tela precisa existir antes do backend para validação com a SMT/DTP
 * (CLAUDE.md, seção "Proibido", exceção de 2026-08-22). Este array some inteiro
 * quando a rota real entrar, e os ganchos abaixo voltam a chamar `chamarApi`.
 */
const PORTAL_PAGINAS_MOCK: PortalPagina[] = [
  {
    id: 'pagina-exemplo-inicio',
    slug: 'inicio',
    titulo: 'Início',
    hero_titulo: 'Transporte Escolar Gratuito',
    hero_descricao:
      'Conheça o TEG, o transporte escolar gratuito da rede municipal de ensino de São Paulo.',
    hero_imagem_url: null,
    meta_title: 'TEG — Transporte Escolar Gratuito',
    meta_description: 'Informações sobre o Transporte Escolar Gratuito da Prefeitura de São Paulo.',
    meta_keywords: 'transporte escolar, TEG, prefeitura de são paulo',
    og_image_url: null,
    canonical_url: null,
    noindex: false,
    jsonld_data: [],
    ordem: 1,
    ativo: true,
    updated_at: new Date(Date.now() - 2 * 86_400_000).toISOString(),
  },
  {
    id: 'pagina-exemplo-como-funciona',
    slug: 'como-funciona',
    titulo: 'Como funciona',
    hero_titulo: 'Como funciona o TEG',
    hero_descricao: 'Entenda as etapas do transporte escolar gratuito, do cadastro ao embarque.',
    hero_imagem_url: null,
    meta_title: 'Como funciona o TEG',
    meta_description: 'Etapas do transporte escolar gratuito da rede municipal de ensino.',
    meta_keywords: null,
    og_image_url: null,
    canonical_url: null,
    noindex: false,
    jsonld_data: [],
    ordem: 2,
    ativo: true,
    updated_at: new Date(Date.now() - 10 * 86_400_000).toISOString(),
  },
  {
    id: 'pagina-exemplo-quem-pode-participar',
    slug: 'quem-pode-participar',
    titulo: 'Quem pode participar',
    hero_titulo: 'Quem pode participar',
    hero_descricao: 'Critérios de elegibilidade para solicitar o transporte escolar gratuito.',
    hero_imagem_url: null,
    meta_title: null,
    meta_description: null,
    meta_keywords: null,
    og_image_url: null,
    canonical_url: null,
    noindex: false,
    jsonld_data: [],
    ordem: 3,
    ativo: true,
    updated_at: new Date(Date.now() - 30 * 86_400_000).toISOString(),
  },
  {
    id: 'pagina-exemplo-duvidas-frequentes',
    slug: 'duvidas-frequentes',
    titulo: 'Dúvidas frequentes',
    hero_titulo: 'Perguntas frequentes',
    hero_descricao: 'Respostas para as dúvidas mais comuns sobre o TEG.',
    hero_imagem_url: null,
    meta_title: null,
    meta_description: null,
    meta_keywords: null,
    og_image_url: null,
    canonical_url: null,
    noindex: false,
    jsonld_data: [],
    ordem: 4,
    ativo: false,
    updated_at: new Date(Date.now() - 90 * 86_400_000).toISOString(),
  },

  /*
   * TEMPORÁRIO — as seis páginas institucionais que o menu público do protótipo
   * cita. Existem aqui para que os endereços do cabeçalho e do rodapé mostrem
   * página, e não "não publicada", antes de a API de conteúdo existir. Saem
   * junto com o resto deste arquivo quando a rota real entrar.
   */
  {
    id: 'pagina-institucional-programa',
    slug: 'programa',
    titulo: 'Sobre o TEG',
    hero_titulo: 'Sobre o Transporte Escolar Gratuito',
    hero_descricao:
      'O que é o programa, quem ele atende e qual o papel da Secretaria Municipal de Mobilidade Urbana e Transporte.',
    hero_imagem_url: null,
    meta_title: 'Sobre o TEG',
    meta_description: 'O programa de Transporte Escolar Gratuito da cidade de São Paulo.',
    meta_keywords: null,
    og_image_url: null,
    canonical_url: null,
    noindex: false,
    jsonld_data: [],
    ordem: 5,
    ativo: true,
    updated_at: new Date(Date.now() - 5 * 86_400_000).toISOString(),
  },
  {
    id: 'pagina-institucional-credenciados',
    slug: 'credenciados',
    titulo: 'Para credenciados',
    hero_titulo: 'Para credenciados',
    hero_descricao:
      'Como se credenciar, quais documentos apresentar e como acompanhar uma inscrição em andamento.',
    hero_imagem_url: null,
    meta_title: 'Para credenciados — TEG',
    meta_description: 'Informações para transportadores interessados no credenciamento.',
    meta_keywords: null,
    og_image_url: null,
    canonical_url: null,
    noindex: false,
    jsonld_data: [],
    ordem: 6,
    ativo: true,
    updated_at: new Date(Date.now() - 5 * 86_400_000).toISOString(),
  },
  {
    id: 'pagina-institucional-transparencia',
    slug: 'transparencia',
    titulo: 'Transparência',
    hero_titulo: 'Transparência',
    hero_descricao: 'Onde encontrar os dados abertos do programa e a legislação que o rege.',
    hero_imagem_url: null,
    meta_title: 'Transparência — TEG',
    meta_description: 'Dados públicos e legislação do Transporte Escolar Gratuito.',
    meta_keywords: null,
    og_image_url: null,
    canonical_url: null,
    noindex: false,
    jsonld_data: [],
    ordem: 7,
    ativo: true,
    updated_at: new Date(Date.now() - 5 * 86_400_000).toISOString(),
  },
  {
    id: 'pagina-institucional-dados-publicos',
    slug: 'dados-publicos',
    titulo: 'Dados Públicos',
    hero_titulo: 'Dados públicos do programa',
    hero_descricao:
      'Consultas abertas sobre credenciados, frota e atendimento, publicadas pela Secretaria.',
    hero_imagem_url: null,
    meta_title: 'Dados Públicos — TEG',
    meta_description: 'Consultas abertas do Transporte Escolar Gratuito.',
    meta_keywords: null,
    og_image_url: null,
    canonical_url: null,
    noindex: false,
    jsonld_data: [],
    ordem: 8,
    ativo: true,
    updated_at: new Date(Date.now() - 5 * 86_400_000).toISOString(),
  },
  {
    id: 'pagina-institucional-legislacao',
    slug: 'legislacao',
    titulo: 'Legislação',
    hero_titulo: 'Legislação do TEG',
    hero_descricao: 'Normas municipais que disciplinam o transporte escolar gratuito.',
    hero_imagem_url: null,
    meta_title: 'Legislação — TEG',
    meta_description: 'Normas municipais do Transporte Escolar Gratuito.',
    meta_keywords: null,
    og_image_url: null,
    canonical_url: null,
    noindex: false,
    jsonld_data: [],
    ordem: 9,
    ativo: true,
    updated_at: new Date(Date.now() - 5 * 86_400_000).toISOString(),
  },
  {
    id: 'pagina-institucional-termos-de-uso',
    slug: 'termos-de-uso',
    titulo: 'Termos de Uso',
    hero_titulo: 'Termos de uso do portal',
    hero_descricao: 'Condições de uso do portal do Transporte Escolar Gratuito.',
    hero_imagem_url: null,
    meta_title: 'Termos de Uso — TEG',
    meta_description: 'Condições de uso do portal do TEG.',
    meta_keywords: null,
    og_image_url: null,
    canonical_url: null,
    noindex: false,
    jsonld_data: [],
    ordem: 10,
    ativo: true,
    updated_at: new Date(Date.now() - 5 * 86_400_000).toISOString(),
  },
];

const PORTAL_SECOES_MOCK: PortalSecao[] = [
  {
    id: 'secao-exemplo-inicio-modalidades',
    pagina_id: 'pagina-exemplo-inicio',
    slug_secao: 'modalidades',
    titulo: 'Modalidades de transporte',
    subtitulo: 'Conheça as opções disponíveis para a sua região',
    tipo: 'cards',
    conteudo: {
      itens: [
        {
          titulo: 'Van escolar',
          descricao: 'Transporte porta a porta para estudantes da rede municipal.',
        },
        {
          titulo: 'Ônibus escolar',
          descricao: 'Rotas fixas que atendem escolas de uma mesma região.',
        },
      ],
    },
    ordem: 1,
    ativo: true,
  },
  {
    id: 'secao-exemplo-inicio-numeros',
    pagina_id: 'pagina-exemplo-inicio',
    slug_secao: 'numeros-do-programa',
    titulo: 'O TEG em números',
    subtitulo: null,
    tipo: 'stats',
    conteudo: {
      itens: [
        { rotulo: 'Estudantes atendidos', valor: '120 mil' },
        { rotulo: 'Veículos credenciados', valor: '3.400' },
      ],
    },
    ordem: 2,
    ativo: true,
  },
  {
    id: 'secao-exemplo-como-funciona-etapas',
    pagina_id: 'pagina-exemplo-como-funciona',
    slug_secao: 'etapas',
    titulo: 'Etapas do processo',
    subtitulo: null,
    tipo: 'timeline',
    conteudo: { itens: [{ titulo: 'Solicitação' }, { titulo: 'Análise' }, { titulo: 'Embarque' }] },
    ordem: 1,
    ativo: true,
  },

  /* TEMPORÁRIO — seções das seis páginas institucionais do menu público. */
  {
    id: 'secao-institucional-programa-o-que-e',
    pagina_id: 'pagina-institucional-programa',
    slug_secao: 'o-que-e',
    titulo: 'O que é o programa',
    subtitulo: null,
    tipo: 'cards',
    conteudo: {
      itens: [
        {
          titulo: 'Quem é atendido',
          descricao:
            'Estudantes da rede municipal de ensino que atendem aos critérios definidos em norma.',
        },
        {
          titulo: 'Quem executa',
          descricao: 'Transportadores credenciados pela Secretaria, por chamamento público.',
        },
        {
          titulo: 'Quem fiscaliza',
          descricao:
            'O Departamento de Transportes Públicos, com apoio das diretorias regionais de ensino.',
        },
      ],
    },
    ordem: 1,
    ativo: true,
  },
  {
    id: 'secao-institucional-credenciados-etapas',
    pagina_id: 'pagina-institucional-credenciados',
    slug_secao: 'etapas-do-credenciamento',
    titulo: 'Etapas do credenciamento',
    subtitulo: null,
    tipo: 'timeline',
    conteudo: {
      itens: [
        { titulo: 'Inscrição', descricao: 'Envio do cadastro e dos documentos exigidos.' },
        { titulo: 'Análise', descricao: 'Conferência documental e vistoria dos veículos.' },
        { titulo: 'Convocação', descricao: 'Habilitação e assinatura do termo de credenciamento.' },
      ],
    },
    ordem: 1,
    ativo: true,
  },
  {
    id: 'secao-institucional-transparencia-onde',
    pagina_id: 'pagina-institucional-transparencia',
    slug_secao: 'o-que-esta-publicado',
    titulo: 'O que está publicado',
    subtitulo: null,
    tipo: 'lista',
    conteudo: {
      itens: [
        { titulo: 'Dados públicos', descricao: 'Consultas abertas sobre credenciados e frota.' },
        { titulo: 'Legislação', descricao: 'Normas municipais que disciplinam o programa.' },
      ],
    },
    ordem: 1,
    ativo: true,
  },
  {
    id: 'secao-institucional-dados-publicos-consultas',
    pagina_id: 'pagina-institucional-dados-publicos',
    slug_secao: 'consultas-disponiveis',
    titulo: 'Consultas disponíveis',
    subtitulo: null,
    tipo: 'lista',
    conteudo: {
      itens: [
        {
          titulo: 'Credenciados em operação',
          descricao: 'Relação dos transportadores com termo vigente.',
        },
        { titulo: 'Frota vistoriada', descricao: 'Veículos com vistoria válida por diretoria.' },
      ],
    },
    ordem: 1,
    ativo: true,
  },
  {
    id: 'secao-institucional-legislacao-normas',
    pagina_id: 'pagina-institucional-legislacao',
    slug_secao: 'normas',
    titulo: 'Normas aplicáveis',
    subtitulo: 'A relação oficial é publicada pela Secretaria.',
    tipo: 'lista',
    conteudo: {
      itens: [
        { titulo: 'Decreto municipal do transporte escolar gratuito' },
        { titulo: 'Portaria de credenciamento de transportadores' },
        { titulo: 'Instrução normativa de vistoria de veículos' },
      ],
    },
    ordem: 1,
    ativo: true,
  },
  {
    id: 'secao-institucional-termos-condicoes',
    pagina_id: 'pagina-institucional-termos-de-uso',
    slug_secao: 'condicoes',
    titulo: 'Condições de uso',
    subtitulo: null,
    tipo: 'texto',
    conteudo: {
      texto:
        'O uso deste portal implica concordância com as condições publicadas pela Secretaria Municipal de Mobilidade Urbana e Transporte. O tratamento de dados pessoais segue a Lei nº 13.709/2018 (LGPD), nos termos do aviso de privacidade.',
    },
    ordem: 1,
    ativo: true,
  },
];

// Retry + backoff para queries do portal público.
// Em prod (cold start do backend / blip de rede móvel) o default global
// `retry: 1` é insuficiente e a página fica com hero/rodapé mas sem o miolo.
const PORTAL_RETRY = 3;
const portalRetryDelay = (attempt: number) => Math.min(1000 * 2 ** attempt, 8000); // 1s, 2s, 4s, max 8s

export function usePortalPaginas() {
  return useQuery<PortalPagina[]>({
    queryKey: ['portal-paginas'],
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => [...PORTAL_PAGINAS_MOCK].sort((a, b) => a.ordem - b.ordem),
    staleTime: 10 * 60_000,
  });
}

export function usePortalPagina(slug: string) {
  return useQuery<PortalPagina | null>({
    queryKey: ['portal-pagina', slug],
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => PORTAL_PAGINAS_MOCK.find((p) => p.slug === slug) ?? null,
    staleTime: 10 * 60_000,
    enabled: !!slug,
    retry: PORTAL_RETRY,
    retryDelay: portalRetryDelay,
  });
}

export function usePortalSecoes(paginaId: string | undefined) {
  return useQuery<PortalSecao[]>({
    queryKey: ['portal-secoes', paginaId],
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () =>
      PORTAL_SECOES_MOCK.filter((s) => s.pagina_id === paginaId).sort((a, b) => a.ordem - b.ordem),
    staleTime: 10 * 60_000,
    enabled: !!paginaId,
    retry: PORTAL_RETRY,
    retryDelay: portalRetryDelay,
  });
}
